import { useEffect } from 'react';

/**
 * Fired on window after a registered "after WhatsApp send" callback runs (success only).
 * Use for cross-cutting concerns; primary flow uses register + complete below.
 */
export const WA_MESSAGE_SEND_COMPLETE = 'wa-message-send-complete';

export type WhatsAppSendCompleteSource = 'iframe' | 'bulk';

export type WhatsAppMessageSendCompleteDetail = {
  success: boolean;
  source: WhatsAppSendCompleteSource;
};

const registry = new Map<string, () => void>();

/**
 * Register a one-shot callback to run when the new WhatsApp UI reports a successful send
 * (single-chat send from iframe, or bulk panel queue success).
 * Returns an opaque token to pass through wa-open-chat / wa-open-bulk → drawer → iframe.
 */
export function registerWhatsAppSendCompleteCallback(fn: () => void): string {
  const id = `wasc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  registry.set(id, fn);
  return id;
}

/**
 * Invoke and remove the callback registered for this token (only if success).
 * Also dispatches {@link WA_MESSAGE_SEND_COMPLETE} for optional global listeners.
 */
export function completeWhatsAppSend(
  token: string | undefined,
  success: boolean,
  source: WhatsAppSendCompleteSource = 'iframe',
): void {
  if (!token) return;
  const fn = registry.get(token);
  registry.delete(token);
  if (success && fn) {
    try {
      fn();
    } catch (e) {
      console.error(e);
    }
  }
  try {
    window.dispatchEvent(
      new CustomEvent<WhatsAppMessageSendCompleteDetail>(WA_MESSAGE_SEND_COMPLETE, {
        detail: { success, source },
      }),
    );
  } catch {
    /* ignore */
  }
}

/**
 * React hook: subscribe to WhatsApp send completions (success or failure from API path).
 * Runs after any registered one-shot callback for that send.
 */
export function useWhatsAppSendComplete(
  handler: (detail: WhatsAppMessageSendCompleteDetail) => void,
): void {
  useEffect(() => {
    const l = (e: Event) => {
      const ce = e as CustomEvent<WhatsAppMessageSendCompleteDetail>;
      if (ce.detail) handler(ce.detail);
    };
    window.addEventListener(WA_MESSAGE_SEND_COMPLETE, l as EventListener);
    return () => window.removeEventListener(WA_MESSAGE_SEND_COMPLETE, l as EventListener);
  }, [handler]);
}
